import {SHADER_CHUNKS, ShaderCreator} from "./OwayoShader";
import {OwayoShaderSourceForSelObCreator, ShaderPrinter} from "./OwayoShaderSourceForSelObCreator";
import type {PartSelectableObject} from "../partSelOb";
import {type BoxRelativeToPart, type OwayoKonfiPart} from "../owayoPartWithSelObs";
import {THREE} from "../resource/BVH.js";
import type {Vector2} from "../delme_globals";

import globalsObject from "../globalsObject";
import {OwayoShaderSourceForOverlayMapCreator} from "./overlayMapShaderCreator";
import {CONSTANTS} from "../meta_data";
import {getShaderMaterial, getShaderUniformsForSingleShader} from "./uniforms";
import {type PartForMaterial} from "../owayoJSViewerWithSelObs";


export interface PartSelObShaderInfoProvider {
    textureSize: Vector2,
    getTexture: () => THREE.Texture | null,
    margin: Vector2,
    getMatrixAbsCanvasFromAbsUV: () => THREE.Matrix3,
    repeat: boolean,
    clampToColor: number,
    wasserzeichen: boolean,
    wasserzeichenInvertImage: boolean,
    wasserzeichenIntensity: number
}

export class SelObShaderUniformCreator {
    public selectableObjectTextures: Vector2[] = [];
    public selectableObjectTexturesSize: Vector2[] = [];
    public selObMargins: Vector2[] = [];
    public selObMatrices: THREE.Matrix3[] = [];
    public selObRepeat: number[] = [];
    public selObClampToColor: number[] = [];
    public selObWasserzeichen: number[] = [];
    public selObWasserzeichenInvertImage: number[] = [];
    public selObWasserzeichenIntensity: number[] = [];

    public addSelOb(selobOrPattern: PartSelObShaderInfoProvider) {
        this.selectableObjectTextures.push(selobOrPattern.getTexture());
        this.selectableObjectTexturesSize.push(new THREE.Vector2(selobOrPattern.textureSize.x, selobOrPattern.textureSize.y));
        this.selObMargins.push(new THREE.Vector2(selobOrPattern.margin.x, selobOrPattern.margin.y));
        this.selObMatrices.push(selobOrPattern.getMatrixAbsCanvasFromAbsUV());
        this.selObRepeat.push(selobOrPattern.repeat ? 1 : 0);
        this.selObClampToColor.push(selobOrPattern.clampToColor);
        this.selObWasserzeichen.push(selobOrPattern.wasserzeichen ? 1 : 0);
        this.selObWasserzeichenInvertImage.push(selobOrPattern.wasserzeichenInvertImage ? 1 : 0);
        this.selObWasserzeichenIntensity.push(selobOrPattern.wasserzeichenIntensity);
    }

    isEmpty = () => this.selectableObjectTextures.length === 0;

    getShaderUniformObject() {
        return {
            "SelectableObjectTexture": {type: "tv", value: this.selectableObjectTextures},
            "SelectableObjectTextureSize": {type: "v2v", value: this.selectableObjectTexturesSize},
            "selObMatrices": {type: "m3v", value: this.selObMatrices},
            "selObMargins": {type: "v2v", value: this.selObMargins},
            "selObRepeat": {type: "iv1", value: this.selObRepeat},
            "selObClampToColor": {type: "iv1", value: this.selObClampToColor},
            // "selObWasserzeichen": {type: "iv1", value: this.selObWasserzeichen},
            // "selObWasserzeichenInvertImage": {type: "iv1", value: this.selObWasserzeichenInvertImage},
            // "selObWasserzeichenIntensity": {type: "fv1", value: this.selObWasserzeichenIntensity}
        }
    }
}


export class SingleShaderCreator {

    public static createSingleShader(part: OwayoKonfiPart, withColors: boolean, patternsAndSelObs: PartSelectableObject[], anzSelObsToDisplay: number, overideFragmentShader: string | null = null) {
        withColors = withColors && (part.parent.colorCodes.length > 0);
        anzSelObsToDisplay = Math.min(patternsAndSelObs.length, anzSelObsToDisplay);
        patternsAndSelObs.forEach(patternOrSelOb => {
            patternOrSelOb.texture.needsUpdate = true;
        })
        const ShaderUniforms = getShaderUniformsForSingleShader(part, withColors, patternsAndSelObs, anzSelObsToDisplay);
        const FragmentShader = SingleShaderCreator.getFragmentShader(part, withColors, anzSelObsToDisplay, overideFragmentShader);
        const shader = getShaderMaterial(ShaderUniforms, FragmentShader, (part.alphaMap as THREE.Texture), withColors);

        if (anzSelObsToDisplay > 0) {
            for (let i = 0; i < anzSelObsToDisplay; i++) {
                patternsAndSelObs[i].shader = shader;
                patternsAndSelObs[i].indexInShader = i;
            }
        }

        shader.bumpMap = (part.bumpMap || part.normalMap);
        return shader;
    }

    protected static getFragmentShader(part: OwayoKonfiPart, withColors: boolean, anzSelObsToDisplay: number, overideFragmentShader: string | null = null) {
        return typeof (overideFragmentShader) === "string" ?
            overideFragmentShader :
            SingleShaderCreator.getFragmentShaderForSingleShader(part, withColors, anzSelObsToDisplay).join('\n');
    }


    protected static getShaderVec2(variableName: string, value: Vector2) {
        return " " + variableName + " = vec2(" + value.x + ',' + value.y + ');';
    }

    private static getSelObUniformShaderDefinition() {
        return ['uniform sampler2D  SelectableObjectTexture[AnzSelectableObjects];',
            'uniform vec2      SelectableObjectTextureSize[AnzSelectableObjects];',
            'uniform mat3      selObMatrices[AnzSelectableObjects];',
            'uniform vec2      selObMargins[AnzSelectableObjects];',
            'uniform int       selObRepeat[AnzSelectableObjects];',
            'uniform int       selObClampToColor[AnzSelectableObjects];',
            // 'uniform int       selObWasserzeichen[AnzSelectableObjects];',
            // 'uniform int       selObWasserzeichenInvertImage[AnzSelectableObjects];',
            // 'uniform float     selObWasserzeichenIntensity[AnzSelectableObjects];'
        ].join("\n");
    }

    protected static getFragmentShaderForSingleShader(part: OwayoKonfiPart, withColors: boolean, anzSelObsToDisplay: number) {
        // Wir erstellen nun einen Fragment-Shader
        return [
            ShaderCreator.addMaterialPreProcessorDirectivesIfNeeded(part),
            '#define AnzColors ' + (withColors ? part.parent.colorCodes.length : 0),
            '#define AnzSelectableObjects ' + (anzSelObsToDisplay.toString()),
            'varying vec2 vUv;',
            'varying vec2 vOriginalUv;',
            'uniform vec4 offsetRepeat;',

            'uniform vec3 diffuse;',
            'uniform vec3 ambient;',
            'uniform vec3 emissive;',

            ShaderCreator.addMaterialFragmentShaderParameterIfNeeded(part),
            THREE.ShaderChunk.common,
            THREE.ShaderChunk.lights_phong_pars_fragment,

            (withColors ? 'uniform sampler2D ColorTextures[2];' : ''),
            (anzSelObsToDisplay > 0 ? SingleShaderCreator.getSelObUniformShaderDefinition() : ''),

            'uniform int isGreyedOut;',
            'uniform int isGreyedOutExceptDruckbereich;',
            "#ifdef USE_OVERLAYMAP",
            "uniform sampler2D overlayMap;",
            "uniform vec2 overlayMapSize;",
            "uniform mat3 overlayScalingMatrix;",
            "#endif",
            SingleShaderCreator.getIsInBoxFunction(),
            'void main(void) {',
            '  vec2 TextureGesamtGroesse;',
            '  vec2 TexturePixelToUvMulti;',
            '  vec2 PixelPositioninOriginal;',
            '  ivec2 PixelPositioninOriginalInt;',
            '  vec2 PixelPositioninOriginalWheight;',
            '  vec2 PixelPositionInSelectableObject;',
            '  vec2 vUvPixel;',
            '  vec2 TMPPixelPositionInSelectableObject;',
            '  vec4 ColorResult;',
            '  vec4 ColorTmp;',
            '  float RotationRadians; ',
            '  vec2 patternpos;',
            '  vec3 posInOverlayMap; ',
            SingleShaderCreator.getShaderVec2("TextureGesamtGroesse", part.textureSize),
            SingleShaderCreator.getShaderVec2("TexturePixelToUvMulti", part.textureSize.apply((a: number) => 1.0 / a)),

            // GetPixelPositionInOriginal
            '  PixelPositioninOriginal = TextureGesamtGroesse * vOriginalUv;',
            '  ColorResult = vec4(1.0,0.0,1.0,0.0); ',
            (withColors ? '' : '  float A = 0.0;'),
            (withColors ? SHADER_CHUNKS.blendPixels.join("\n") : ''),
            SingleShaderCreator.getOverlayMapFragmentShader(part),
            SingleShaderCreator.getSelObsFragmentShader(anzSelObsToDisplay, globalsObject.owayoJSViewer.partIsDropTarget(part), part.druckBereich, part.schwarzAufhellenImDruckbereich, part.nichtDruckBereich),

            SHADER_CHUNKS.brightenBlack.join("\n"),
            // GrayOut durchführen
            ' ColorResult = vec4( mix( ColorResult.rgb, vec3( 0.1,0.1,0.1 ), 0.5 * float( isGreyedOut ) ), ColorResult.a);',
            //ColorResult.a * (1.0 - 0.5 * float(isGreyedOut)) );',
            //' ColorResult = vec4(0.5,0.5,0.5, ColorResult.a);',
            // Innenseite aufhellen.
            part.innenseiteAufhellen ? ' gl_FragColor = vec4(mix(ColorResult.xyz,vec3(1,1,1),0.6*(1.0-float(gl_FrontFacing))),ColorResult.a ); ' :
                ' gl_FragColor = vec4(mix(ColorResult.xyz,vec3(1,1,1),0.0),ColorResult.a ); '
            ,

            (withColors ?
                globalsObject.owayoJSViewer.partIsDropTarget(part) || anzSelObsToDisplay > 0 ?
                    '  gl_FragColor.a = 1.0;'
                    :
                    '  gl_FragColor.a = 1.0 - 0.1 * float(isGreyedOut); if (isGreyedOut > 0) {return;}'
                :
                'if (gl_FragColor.a > 1.0) { gl_FragColor.a = 1.0; }'),
            ShaderCreator.addFragmentMaterialAndLighting(part),
            '}'
        ];
    }

    private static getIsInBoxFunction() {
        const p = ShaderPrinter;

        return ['bool isInBox(vec2 pos, vec2 topright, vec2 bottomleft) {',
            ' return ' + p.and(
                p.compare("pos.x", "topright.x", "<"),
                p.compare("pos.x", "bottomleft.x", ">"),
                p.compare("pos.y", p.subtract("1.0", "topright.y"), "<"),
                p.compare("pos.y", p.subtract("1.0", "bottomleft.y"), ">")
            ) + ";",
            "}"
        ].join("\n");


    }

    private static getSelObsFragmentShader(anzSelObsToDisplay: number, partIsDroppbale: boolean, druckBereich: BoxRelativeToPart | null = null, schwarzAufhellenImDruckbereich = false, nichtDruckBereich: BoxRelativeToPart | null = null) {
        // FIXME: Wir brauchen hier eigentlich keine Texturgröße
        //        Uns reicht eine relative-Margin auf der UV.
        //        Also: margin/selob.size
        //        Und eine Matrix die auf die UV abgestimmt ist und nicht auf die textur größe
        //        Dann kann man einfach UV*Matrix nehmen.
        //        Rotation und skalierung bleibt gleich nur die translation muss beim Matrix berechnen anders gemacht werden.
        //        oder? @fischi: was meinst du.
        //        Vorteil: - eine division weniger (die sind extrem teuer)
        //                 - ein uniform array weniger.
        let result: string[] = [];
        if (anzSelObsToDisplay > 0) {
            //wenn ein OM das Logo wegfärbt, dann sollen die Logos auch nicht mehr über allem liegen.
            // evtl. sollte man das auf den Part beschränken, auf dem es eine Veränderung gab...
            if (ShaderCreator.owayoLogosOnTop() && partIsDroppbale) {
                result.push('  if ( A < ' + CONSTANTS.LOWERLIMIT_COLORCODENR_OVER_SELOBS + ') {');
            }

            result.push('    vec3 posInSelOb; ');


            for (let index = 0; index < anzSelObsToDisplay; index++) {
                result = result.concat(OwayoShaderSourceForSelObCreator.getShaderSource(index, druckBereich, schwarzAufhellenImDruckbereich, nichtDruckBereich));
            }

            if (ShaderCreator.owayoLogosOnTop() && partIsDroppbale) {
                result.push('  } ');
            }
        }
        return result.join("\n");
    }


    protected static getOverlayMapFragmentShader(part: PartForMaterial): string {
        if (!part.overlayMap) {
            return "";
        }
        return OwayoShaderSourceForOverlayMapCreator.getShaderSource(part.overlayHelligkeitsSummand, part.overlayIntensity, part.overlayAnzeigenAufFarbenIntArray, part);
    }
}
